import WolfTooltip from '@components/ui/WolfTooltip';
import { graphQlClient } from '@config/graphqlClient';
import ProcessVersionGraphQL from '@graphql/processVersion.queries';
import { useTranslation } from '@hooks/useTranslation';
import { getProcessVersionsRequest, ProcessVersion } from '@models/processVersion.model';
import { Email, InfoOutlined } from '@mui/icons-material';
import { Box, CircularProgress, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { DateTime } from 'luxon';
import React, { useEffect, useState } from 'react';

interface ProcessVersionHistoryProps {
  processId: string;
  currentVersion?: string;
  lastUpdatedAt?: string;
}

const ProcessVersionHistory: React.FC<ProcessVersionHistoryProps> = ({ processId, currentVersion, lastUpdatedAt }) => {
  const [versions, setVersions] = useState<ProcessVersion[]>([]);
  const [loading, setLoading] = useState(true);
  const localeProcesses = useTranslation('processes');

  const CONSOLIDATION_WINDOW = 4 * 60 * 60 * 1000; // 30 minutes

  const isVersionActive = (createdAt: string, bumpType: string, isCurrentVersion: boolean): boolean => {
    // Only the current version can be active
    if (!isCurrentVersion) return false;
    if (bumpType === 'INITIAL') return false;

    const now = new Date();
    const versionCreatedAt = new Date(createdAt);
    const timeDiff = versionCreatedAt.getTime() + CONSOLIDATION_WINDOW;

    return now.getTime() < timeDiff;

  };

  useEffect(() => {
    const fetchVersions = async () => {
      try {
        setLoading(true);
        const response = await graphQlClient.request<getProcessVersionsRequest>(
          ProcessVersionGraphQL.queries.getProcessVersions,
          { processId }
        );
        setVersions(response.getProcessVersions || []);
      } catch (error) {
        console.error('Error fetching process versions:', error);
        setVersions([]);
      } finally {
        setLoading(false);
      }
    };

    if (processId) {
      fetchVersions();
    }
  }, [processId, currentVersion, lastUpdatedAt]);

  if (loading) {
    return (
      <Box className="flex justify-center items-center p-8">
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (versions.length === 0) {
    return null;
  }

  return (
    <Box className="bg-white px-8 py-4">
      <Box className="flex items-center gap-2 mb-3">
        <Typography variant="h6">
          {localeProcesses['versionHistory']}
        </Typography>
        <WolfTooltip title={localeProcesses['versionHistoryTooltip']}>
          <InfoOutlined sx={{ fontSize: 18, color: 'text.secondary' }} />
        </WolfTooltip>
      </Box>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width="100px" sx={{ fontWeight: 600 }}>
                <Box className="flex items-center gap-1">
                  {localeProcesses['version']}
                  <WolfTooltip title={localeProcesses['versionColumnTooltip']}>
                    <InfoOutlined sx={{ fontSize: 14, color: 'text.secondary' }} />
                  </WolfTooltip>
                </Box>
              </TableCell>
              <TableCell sx={{ fontWeight: 600 }}>
                {localeProcesses['versionDescription']}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {versions.map((version, index) => (
              <TableRow key={version.processVersionId} hover>
                <TableCell width="100px">
                  <Typography variant="body2" className="font-semibold">
                    v{version.version}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box>
                    <Box className="flex items-center gap-2 mb-1">
                      <Typography variant="caption" color="textSecondary">
                        {DateTime.fromISO(version.createdAt).toFormat('dd-MM-yyyy')}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        •
                      </Typography>
                      <Box className="flex items-center gap-1">
                        <Typography variant="caption" color="textSecondary">
                          {localeProcesses['versionCreatedBy']}: {version.createdByUser.firstName} {version.createdByUser.lastName}
                        </Typography>
                        <WolfTooltip title={version.createdByUser.email}>
                          <Email sx={{ fontSize: 14, color: 'text.secondary' }} />
                        </WolfTooltip>
                      </Box>
                      {index === 0 && (
                        <>
                          <Typography variant="caption" color="textSecondary">
                            •
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 600 }}>
                            {localeProcesses['currentVersion']}
                          </Typography>
                          {isVersionActive(version.createdAt, version.bumpType, index === 0) && (
                            <Typography variant="caption" color="textSecondary">
                              -
                            </Typography>
                          )}
                        </>
                      )}
                      {isVersionActive(version.createdAt, version.bumpType, index === 0) && (
                        <Box className="flex items-center gap-1">
                          <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 600 }}>
                            {localeProcesses['active']}
                          </Typography>
                          <WolfTooltip title={localeProcesses['activeVersionTooltip']}>
                            <InfoOutlined sx={{ fontSize: 12, color: 'success.main' }} />
                          </WolfTooltip>
                        </Box>
                      )}
                    </Box>
                    <Box>
                      {(() => {
                        const parts = version.changeDescription.split(/(---|\*\*\*)/);
                        return parts.map((part, partIndex) => {
                          const trimmedPart = part.trim();
                          // Skip empty parts and the separators themselves
                          if (!trimmedPart || trimmedPart === '---' || trimmedPart === '***') return null;

                          // Check if the previous part was ***
                          const previousPart = partIndex > 0 ? parts[partIndex - 1] : null;
                          const hasPadding = previousPart === '***';

                          // Check if line starts with patterns like "1.A" or "1.1" (sub-items)
                          const isSubItem = /^\d+\.[A-Za-z0-9]/.test(trimmedPart);

                          // Determine padding class
                          let paddingClass = '';
                          if (hasPadding && isSubItem) {
                            paddingClass = 'pl-8'; // Extra padding for sub-items after ***
                          } else if (hasPadding) {
                            paddingClass = 'pl-4'; // Normal padding after ***
                          } else if (isSubItem) {
                            paddingClass = 'pl-4'; // Padding for sub-items without ***
                          }

                          return (
                            <Typography key={partIndex} variant="body2" className={paddingClass}>
                              {trimmedPart}
                            </Typography>
                          );
                        });
                      })()}
                    </Box>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default ProcessVersionHistory;
